/**
 * LoadingSpinner Component
 * Reusable loading spinner with customizable size and message
 */
import { Box, CircularProgress, Typography, Paper } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

const LoadingSpinner = ({
  size = 40,
  message,
  variant = "contained",
  fullScreen = false,
  color = "primary",
}) => {
  const theme = useTheme();
  const { t } = useTranslation('layoutTranslation');
  const displayMessage = message ?? t("common.loadingPage", "Loading...");


  const LoadingContent = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        p: fullScreen ? 4 : 2,
      }}
    >
      <CircularProgress size={size} color={color} />
      {displayMessage && (
        <Typography variant="body2" color="text.secondary" textAlign="center">
          {displayMessage}
        </Typography>
      )}
    </Box>
  );

  if (fullScreen) {
    return (
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          zIndex: theme.zIndex.modal,
        }}
      >
        {variant === "contained" ? (
          <Paper elevation={3} sx={{ borderRadius: 2 }}>
            {LoadingContent}
          </Paper>
        ) : (
          LoadingContent
        )}
      </Box>
    );
  }

  if (variant === "contained") {
    return (
      <Paper
        elevation={1}
        sx={{
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {LoadingContent}
      </Paper>
    );
  }

  return LoadingContent;
};

LoadingSpinner.propTypes = {
  size: PropTypes.number,
  message: PropTypes.string,
  variant: PropTypes.oneOf(["contained", "minimal"]),
  fullScreen: PropTypes.bool,
  color: PropTypes.string,
};

// Page-level loader
export const PageLoader = ({ message }) => {
  return (
    <LoadingSpinner
      size={50}
      message={message}
      fullScreen={true}
      variant="contained"
    />
  );
};

PageLoader.propTypes = {
  message: PropTypes.string,
};

// Inline loader
export const InlineLoader = ({ size = 20, message = null }) => (
  <LoadingSpinner size={size} message={message} variant="minimal" />
);

InlineLoader.propTypes = {
  size: PropTypes.number,
  message: PropTypes.string,
};

export default LoadingSpinner;
